import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../hooks/redux';
import demographicsService, {
  DashboardData,
  GeographicData,
  DeviceData,
  ReferralData,
  DemographicBreakdownData,
} from '../../../services/demographicsService';

const DemographicsPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'geographic' | 'devices' | 'referrals' | 'demographics'>('overview');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadDashboard();
  }, [user, navigate]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await demographicsService.getDashboard(user!.id);
      setDashboardData(data);
    } catch (err: any) {
      console.error('Error loading demographics:', err);
      setError(err.response?.data?.message || 'Failed to load demographics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading demographics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button
              onClick={loadDashboard}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Demographics & Analytics</h1>
          <p className="mt-2 text-gray-600">
            Understand your audience with geographic, device, and demographic insights
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <SummaryCard
            title="Total Patrons"
            value={dashboardData.summary.totalPatrons.toLocaleString()}
            icon="👥"
            color="blue"
          />
          <SummaryCard
            title="Total Revenue"
            value={`$${dashboardData.summary.totalRevenue.toFixed(2)}`}
            icon="💰"
            color="green"
          />
          <SummaryCard
            title="Top Country"
            value={dashboardData.summary.topCountry || 'N/A'}
            icon="🌍"
            color="purple"
          />
          <SummaryCard
            title="Top Device"
            value={dashboardData.summary.topDevice || 'N/A'}
            icon="📱"
            color="orange"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <TabButton
                active={activeTab === 'overview'}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </TabButton>
              <TabButton
                active={activeTab === 'geographic'}
                onClick={() => setActiveTab('geographic')}
              >
                Geographic
              </TabButton>
              <TabButton
                active={activeTab === 'devices'}
                onClick={() => setActiveTab('devices')}
              >
                Devices
              </TabButton>
              <TabButton
                active={activeTab === 'referrals'}
                onClick={() => setActiveTab('referrals')}
              >
                Referrals
              </TabButton>
              <TabButton
                active={activeTab === 'demographics'}
                onClick={() => setActiveTab('demographics')}
              >
                Demographics
              </TabButton>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'overview' && <OverviewTab data={dashboardData} />}
            {activeTab === 'geographic' && <GeographicTab data={dashboardData.geographic} />}
            {activeTab === 'devices' && <DevicesTab data={dashboardData.devices} />}
            {activeTab === 'referrals' && <ReferralsTab data={dashboardData.referrals} />}
            {activeTab === 'demographics' && <DemographicsTab data={dashboardData.demographics} />}
          </div>
        </div>
      </div>
    </div>
  );
};

// Summary Card Component
interface SummaryCardProps {
  title: string;
  value: string;
  icon: string;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <span className="text-2xl">{icon}</span>
        </div>
      </div>
    </div>
  );
};

// Tab Button Component
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-6 py-3 font-medium text-sm ${
      active
        ? 'border-b-2 border-blue-600 text-blue-600'
        : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`}
  >
    {children}
  </button>
);

// Overview Tab
const OverviewTab: React.FC<{ data: DashboardData }> = ({ data }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {/* Top Countries */}
    <div className="bg-gray-50 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Countries</h3>
      <div className="space-y-3">
        {data.geographic.countries.slice(0, 5).map((country, index) => (
          <div key={index} className="flex items-center justify-between">
            <span className="text-gray-700">{country.country}</span>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">{country.count} patrons</span>
              <span className="text-sm font-medium text-blue-600">{country.percentage.toFixed(1)}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Top Devices */}
    <div className="bg-gray-50 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Distribution</h3>
      <div className="space-y-3">
        {data.devices.devices.slice(0, 5).map((device, index) => (
          <div key={index} className="flex items-center justify-between">
            <span className="text-gray-700">{device.device}</span>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">{device.count} patrons</span>
              <span className="text-sm font-medium text-blue-600">{device.percentage.toFixed(1)}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Top Referral Sources */}
    <div className="bg-gray-50 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Referral Sources</h3>
      <div className="space-y-3">
        {data.referrals.sources.slice(0, 5).map((source, index) => (
          <div key={index} className="flex items-center justify-between">
            <span className="text-gray-700">{source.source}</span>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">{source.count} patrons</span>
              <span className="text-sm font-medium text-green-600">${source.revenue.toFixed(2)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Age Distribution */}
    <div className="bg-gray-50 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Age Distribution</h3>
      <div className="space-y-3">
        {data.demographics.ageRanges.slice(0, 5).map((range, index) => (
          <div key={index} className="flex items-center justify-between">
            <span className="text-gray-700">{range.range}</span>
            <div className="flex items-center">
              <span className="text-sm text-gray-600 mr-2">{range.count} patrons</span>
              <span className="text-sm font-medium text-purple-600">{range.percentage.toFixed(1)}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Geographic Tab
const GeographicTab: React.FC<{ data: GeographicData }> = ({ data }) => {
  return (
    <div className="space-y-8">
      {/* Countries Chart */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Geographic Distribution</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white p-4 rounded-lg">
            <div className="space-y-3">
              {data.countries.slice(0, 10).map((country, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{country.country}</span>
                    <span className="text-sm text-gray-600">{country.percentage.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${country.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {data.countries.map((country, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="font-medium text-gray-900">{country.country}</span>
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-900">{country.count} patrons</div>
                  <div className="text-xs text-gray-600">{country.percentage.toFixed(1)}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Top Regions */}
      {data.regions.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Regions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.regions.slice(0, 9).map((region, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="font-medium text-gray-900">{region.region}</div>
                <div className="text-sm text-gray-600">{region.country}</div>
                <div className="mt-2 text-lg font-semibold text-blue-600">{region.count} patrons</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Cities */}
      {data.cities.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Cities</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.cities.slice(0, 9).map((city, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="font-medium text-gray-900">{city.city}</div>
                <div className="text-sm text-gray-600">{city.region}, {city.country}</div>
                <div className="mt-2 text-lg font-semibold text-blue-600">{city.count} patrons</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Devices Tab
const DevicesTab: React.FC<{ data: DeviceData }> = ({ data }) => {
  return (
    <div className="space-y-8">
      {/* Mobile vs Desktop */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Mobile vs Desktop</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-4xl font-bold text-blue-600">{data.mobileVsDesktop.mobile}</div>
            <div className="text-sm text-gray-600">Mobile Users</div>
            <div className="text-xs text-gray-500 mt-1">{data.mobileVsDesktop.mobilePercentage.toFixed(1)}%</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-purple-600">{data.mobileVsDesktop.desktop}</div>
            <div className="text-sm text-gray-600">Desktop Users</div>
            <div className="text-xs text-gray-500 mt-1">{(100 - data.mobileVsDesktop.mobilePercentage).toFixed(1)}%</div>
          </div>
        </div>
      </div>

      {/* Device Types */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Distribution</h3>
        <div className="space-y-3">
          {data.devices.map((device, index) => (
            <div key={index}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">{device.device}</span>
                <span className="text-sm text-gray-600">{device.percentage.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-600 h-3 rounded-full"
                  style={{ width: `${device.percentage}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Browsers */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Browser Distribution</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.browsers.slice(0, 10).map((browser, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">{browser.browser}</span>
                <span className="text-sm font-semibold text-blue-600">{browser.percentage.toFixed(1)}%</span>
              </div>
              <div className="mt-2 text-sm text-gray-600">{browser.count} users</div>
            </div>
          ))}
        </div>
      </div>

      {/* Operating Systems */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Operating Systems</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.operatingSystems.slice(0, 10).map((os, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">{os.os}</span>
                <span className="text-sm font-semibold text-green-600">{os.percentage.toFixed(1)}%</span>
              </div>
              <div className="mt-2 text-sm text-gray-600">{os.count} users</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Referrals Tab
const ReferralsTab: React.FC<{ data: ReferralData }> = ({ data }) => (
  <div className="space-y-8">
    {/* Summary */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-blue-50 rounded-lg p-6">
        <div className="text-sm text-blue-600 font-medium">Total Patrons</div>
        <div className="text-3xl font-bold text-blue-900 mt-2">{data.totalPatrons.toLocaleString()}</div>
      </div>
      <div className="bg-green-50 rounded-lg p-6">
        <div className="text-sm text-green-600 font-medium">Total Revenue</div>
        <div className="text-3xl font-bold text-green-900 mt-2">${data.totalRevenue.toFixed(2)}</div>
      </div>
    </div>

    {/* Referral Sources */}
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Referral Sources</h3>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patrons</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Percentage</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.sources.map((source, index) => (
              <tr key={index}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{source.source}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{source.count}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{source.percentage.toFixed(1)}%</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">${source.revenue.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Campaign Performance */}
    {data.campaigns.length > 0 && (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Performance</h3>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patrons</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.campaigns.map((campaign, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{campaign.campaign}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{campaign.source}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{campaign.count}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">${campaign.revenue.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

// Demographics Tab
const DemographicsTab: React.FC<{ data: DemographicBreakdownData }> = ({ data }) => {
  return (
    <div className="space-y-8">
      {/* Age Distribution */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Age Distribution</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-3">
            {data.ageRanges.map((range, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{range.range}</span>
                  <span className="text-sm text-gray-600">{range.percentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-purple-600 h-3 rounded-full"
                    style={{ width: `${range.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {data.ageRanges.map((range, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="font-medium text-gray-900">{range.range}</span>
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-900">{range.count} patrons</div>
                  <div className="text-xs text-gray-600">{range.percentage.toFixed(1)}%</div>
                  <div className="text-xs text-green-600">${range.revenue.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gender Distribution */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Gender Distribution</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-3">
            {data.genders.map((gender, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{gender.gender}</span>
                  <span className="text-sm text-gray-600">{gender.percentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-pink-600 h-3 rounded-full"
                    style={{ width: `${gender.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {data.genders.map((gender, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="font-medium text-gray-900">{gender.gender}</span>
                <div className="text-right">
                  <div className="text-sm font-semibold text-gray-900">{gender.count} patrons</div>
                  <div className="text-xs text-gray-600">{gender.percentage.toFixed(1)}%</div>
                  <div className="text-xs text-green-600">${gender.revenue.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Language Distribution */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Language Preferences</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.languages.map((language, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4">
              <div className="font-medium text-gray-900">{language.language}</div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-gray-600">{language.count} patrons</span>
                <span className="text-sm font-semibold text-blue-600">{language.percentage.toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DemographicsPage;
